import { Button } from "@tellescope/ui";
import { Badge, Card } from "@tellescope/design-system";
import { formatDate, formatCurrency, formatCardBrand, formatStripeSubscriptionInterval } from "@tellescope/utils";
import { useTranslation } from "react-i18next";
import {
  CreditCard,
  Calendar,
  CheckCircle,
  AlertCircle,
  Clock,
  XCircle,
  Pause,
  Play,
  RotateCcw,
  Settings,
} from "lucide-react";
import {
  isSubscriptionPaused,
  getSubscriptionResumesAt,
  getStripePlanProductName,
  getStripePlanProductImage,
  type StripeSubscription,
} from "../../../hooks/useStripeSubscriptions";
import type { StripeSubscriptionsBlock } from "../../../../shared/schema";
import { useActionAvailability, getQuickAction } from "./useActionAvailability";
import { useSubscriptionPricing } from "./useSubscriptionPricing";
import { i18n } from "@tellescope/i18n";

interface SubscriptionCardProps {
  subscription: StripeSubscription;
  blockProps: StripeSubscriptionsBlock["props"];
  onManageClick: (subscription: StripeSubscription) => void;
  onQuickAction: (subscription: StripeSubscription, action: "resume" | "update_payment" | "undo_cancel") => void;
  isActionPending?: boolean;
}

// Map status to icon, color, and label
function getStatusInfo(status: StripeSubscription["status"]): {
  icon: React.ElementType;
  colorClass: string;
  badgeVariant: "solid" | "outline";
  badgeColor: "primary" | "secondary" | "destructive";
  label: string;
} {
  switch (status) {
    case "active":
      return {
        icon: CheckCircle,
        colorClass: "text-green-600",
        badgeVariant: "solid",
        badgeColor: "primary",
        label: i18n.t("Active", { ns: "purchases" }),
      };
    case "trialing":
      return {
        icon: Clock,
        colorClass: "text-blue-600",
        badgeVariant: "solid",
        badgeColor: "secondary",
        label: i18n.t("Trial", { ns: "purchases" }),
      };
    case "past_due":
      return {
        icon: AlertCircle,
        colorClass: "text-yellow-600",
        badgeVariant: "outline",
        badgeColor: "primary",
        label: i18n.t("Past Due", { ns: "purchases" }),
      };
    case "unpaid":
      return {
        icon: AlertCircle,
        colorClass: "text-orange-600",
        badgeVariant: "outline",
        badgeColor: "primary",
        label: i18n.t("Unpaid", { ns: "purchases" }),
      };
    case "canceled":
      return {
        icon: XCircle,
        colorClass: "text-gray-500",
        badgeVariant: "solid",
        badgeColor: "secondary",
        label: i18n.t("Canceled", { ns: "purchases" }),
      };
    case "incomplete":
    case "incomplete_expired":
      return {
        icon: AlertCircle,
        colorClass: "text-red-600",
        badgeVariant: "solid",
        badgeColor: "destructive",
        label: i18n.t("Incomplete", { ns: "purchases" }),
      };
    case "paused":
      return {
        icon: Pause,
        colorClass: "text-gray-500",
        badgeVariant: "solid",
        badgeColor: "secondary",
        label: i18n.t("Paused", { ns: "purchases" }),
      };
    default:
      return {
        icon: Clock,
        colorClass: "text-muted-foreground",
        badgeVariant: "outline",
        badgeColor: "primary",
        label: status,
      };
  }
}

export function SubscriptionCard({
  subscription,
  blockProps,
  onManageClick,
  onQuickAction,
  isActionPending = false,
}: SubscriptionCardProps) {
  const { t } = useTranslation("payments");
  const isPaused = isSubscriptionPaused(subscription);
  // Show "paused" status when pause_collection is set, even if status is "active"
  const displayStatus = isPaused ? "paused" : subscription.status;
  const statusInfo = getStatusInfo(displayStatus);
  const StatusIcon = statusInfo.icon;
  const availability = useActionAvailability(subscription, blockProps);
  const quickAction = getQuickAction(subscription, availability);

  const { effectivePlan, resolvedItems } = subscription;
  const isMultiItem = resolvedItems.length > 1;

  // Single-item: use effectivePlan (has expanded product). Multi-item: derive from resolvedItems.
  const productName = getStripePlanProductName(effectivePlan) ?? t("Subscription");
  const productImage = getStripePlanProductImage(effectivePlan);

  const pricing = useSubscriptionPricing(subscription);
  const price =
    resolvedItems.length > 0 ? formatCurrency(pricing.listTotal, pricing.currency, { fromSmallestUnit: true }) : null;
  const discountedPrice =
    pricing.hasDiscount && pricing.discountedTotal !== null
      ? formatCurrency(pricing.discountedTotal, pricing.currency, { fromSmallestUnit: true })
      : null;
  const primaryDiscount = pricing.additionalDiscountCount === 0 ? pricing.discounts[0] ?? null : null;
  const interval = effectivePlan
    ? formatStripeSubscriptionInterval(effectivePlan.interval, effectivePlan.interval_count)
    : resolvedItems[0]
    ? formatStripeSubscriptionInterval(resolvedItems[0].interval, resolvedItems[0].interval_count)
    : null;
  const nextBillingDate = formatDate(subscription.current_period_end * 1000); // Stripe uses Unix seconds
  const resumesAt = getSubscriptionResumesAt(subscription);
  const pausedUntilDate = resumesAt !== null ? formatDate(resumesAt * 1000) : null;

  const paymentMethod = subscription.default_payment_method;
  const cardInfo = paymentMethod?.card;

  const handleQuickAction = () => {
    if (quickAction) {
      onQuickAction(subscription, quickAction);
    }
  };

  const getQuickActionButton = () => {
    if (!quickAction) return null;

    switch (quickAction) {
      case "resume":
        return (
          <Button variant="default" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <Play className="h-3 w-3 mr-1" />
            {t("Resume")}
          </Button>
        );
      case "update_payment":
        return (
          <Button variant="outline" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <CreditCard className="h-3 w-3 mr-1" />
            {t("Update Payment")}
          </Button>
        );
      case "undo_cancel":
        return (
          <Button variant="outline" size="sm" onClick={handleQuickAction} disabled={isActionPending}>
            <RotateCcw className="h-3 w-3 mr-1" />
            {t("Reactivate Subscription")}
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <Card variant="floating" padding="root" className="overflow-hidden">
      <Card.Body className="px-4">
        <div className="flex gap-4">
          {/* Product image or status icon */}
          <div className="flex-shrink-0 text-center">
            {productImage ? (
              <img src={productImage} alt={productName} className="w-14 h-14 rounded-lg object-cover" />
            ) : (
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex flex-col items-center justify-center">
                <StatusIcon className={`h-6 w-6 ${statusInfo.colorClass}`} />
              </div>
            )}
          </div>

          {/* Subscription details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h4 className="font-medium text-sm truncate">
                {isMultiItem ? t("Multi-Product Subscription") : productName}
              </h4>
              <Badge variant={statusInfo.badgeVariant} color={statusInfo.badgeColor}>
                {statusInfo.label}
              </Badge>
            </div>

            {/* Multi-item: list each product name and price */}
            {isMultiItem && (
              <div className="mt-1 space-y-0.5">
                {pricing.itemBreakdown.map((item) => {
                  const itemHasDiscount = item.discountedAmount !== null && item.discountedAmount < item.listAmount;
                  return (
                    <div
                      key={item.subscriptionItemId ?? item.productName}
                      className="flex items-center justify-between text-xs text-muted-foreground"
                    >
                      <span>{item.productName || t("Subscription")}</span>
                      <span>
                        {itemHasDiscount && (
                          <span className="line-through mr-1">
                            {formatCurrency(item.listAmount, item.currency, { fromSmallestUnit: true })}
                          </span>
                        )}
                        {formatCurrency(item.discountedAmount ?? item.listAmount, item.currency, {
                          fromSmallestUnit: true,
                        })}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {price !== null && (
              <div className="flex items-center gap-2 text-sm mt-1">
                <span className="font-semibold text-primary">
                  {pricing.hasDiscount && discountedPrice !== null ? (
                    <span className="line-through text-muted-foreground text-xs font-normal">{price}</span>
                  ) : isMultiItem ? (
                    t("Total: {{price}}", { price })
                  ) : (
                    price
                  )}
                  <span className="text-muted-foreground font-normal">{interval}</span>
                </span>
              </div>
            )}

            {pricing.hasDiscount && discountedPrice !== null && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("Next payment: {{price}}", { price: discountedPrice })}
              </div>
            )}

            {pricing.hasDiscount && pricing.primaryDiscountLabel !== null && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {pricing.additionalDiscountCount > 0
                  ? t("{{name}} + {{count}} more discounts", {
                      name: pricing.primaryDiscountLabel,
                      count: pricing.additionalDiscountCount,
                    })
                  : primaryDiscount !== null && primaryDiscount.percentOff !== null
                  ? t("{{name}} ({{percentOff}}% off)", {
                      name: pricing.primaryDiscountLabel,
                      percentOff: primaryDiscount.percentOff,
                    })
                  : primaryDiscount !== null && primaryDiscount.amountOff !== null
                  ? t("{{name}} ({{amountOff}} off)", {
                      name: pricing.primaryDiscountLabel,
                      amountOff: formatCurrency(primaryDiscount.amountOff, pricing.currency, {
                        fromSmallestUnit: true,
                      }),
                    })
                  : pricing.primaryDiscountLabel}
              </div>
            )}

            {pricing.hasDiscount && pricing.discountExpiration?.type === "expiring" && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("Price increases to {{price}} on {{date}}", {
                  price,
                  date: formatDate(pricing.discountExpiration.date * 1000),
                })}
              </div>
            )}

            {pricing.hasDiscount && pricing.discountExpiration?.type === "expiring-soon" && (
              <div className="text-xs text-muted-foreground mt-0.5">
                {t("This discount applies to your next invoice only")}
              </div>
            )}

            {pricing.trialEndsAt !== null && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Clock className="h-3 w-3" />
                <span>
                  {t("Trial until {{date}}, then {{price}}", {
                    date: formatDate(pricing.trialEndsAt * 1000),
                    price: discountedPrice ?? price ?? "",
                  })}
                </span>
              </div>
            )}

            {pricing.hasSchedule && (
              <div className="text-xs text-muted-foreground mt-1">{t("Your plan has an upcoming change")}</div>
            )}

            {/* Next billing date - don't show when paused or scheduled for cancellation */}
            {subscription.status === "active" && !subscription.cancel_at_period_end && !isPaused && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <Calendar className="h-3 w-3" />
                <span>
                  {t("Next billing")}: {nextBillingDate}
                </span>
              </div>
            )}

            {/* Paused notice */}
            {isPaused && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                {pausedUntilDate !== null ? (
                  <>
                    <Calendar className="h-3 w-3" />
                    <span>{t("Paused until {{date}}", { date: pausedUntilDate })}</span>
                  </>
                ) : (
                  <span>{t("Subscription is paused")}</span>
                )}
              </div>
            )}

            {/* Cancellation notice */}
            {subscription.cancel_at_period_end && (
              <div className="text-xs text-muted-foreground mt-1">
                {t("Cancels on {{date}}", { date: nextBillingDate })}
              </div>
            )}

            {/* Payment method */}
            {cardInfo && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                <CreditCard className="h-3 w-3" />
                <span>
                  {formatCardBrand(cardInfo.brand)} ****{cardInfo.last4}
                </span>
              </div>
            )}

            {/* Action buttons */}
            {availability.hasAnyAction && (
              <div className="flex items-center gap-2 mt-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onManageClick(subscription)}
                  disabled={isActionPending}
                >
                  <Settings className="h-3 w-3 mr-1" />
                  {t("Manage")}
                </Button>
                {getQuickActionButton()}
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
